import React, { useState, useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import {Layout, Row, Col, message} from 'antd';
import { useAuth } from './hooks/auth';
import { mainMenuItems, getAdminMenuItems, routeItems, routeAuthItems } from './navigations'
import {getNotifications, markNotificationsAsRead} from './services/adminApi';
import MainMenu from './MainMenu';
import { deleteTestSession, getTestSession, TestSession } from './utils/testSession';
import ConfirmationModal from './components/ConfirmationModal';

const { Header, Content, Footer } = Layout;

const content = [24, 22, 20, 18, 18];
const margin = [0, 1, 2, 3, 3];

const App: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const adminMenuItems = getAdminMenuItems(user.role, isAdmin, user.extraData?.permissions, user.type);
  const [isAdminMode, setIsAdminMode] = useState(adminMenuItems.map(x => x.to).includes(window.location.pathname));

  const [unapprovedPrizeCount, setUnapprovedPrizeCount] = useState(0);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [unfinishedTest, setUnfinishedTest] = useState<(TestSession & { secondsLeft: number }) | null>(null);

  useEffect(() => {
    if (!isAdmin) setIsAdminMode(false);
    if (user?.id) {
      getNotifications().then(({ unapprovedPrizeCount, notifications }) => {
        setUnapprovedPrizeCount(unapprovedPrizeCount);
        setNotifications(notifications);
      }).catch(err => {
        console.error('Error fetching notifications:', err);
      });
    }
  }, [isAdmin, user?.id])

  const unreadNotificationsCount = notifications.filter(notification => notification.unread).length;

  const noticesCount = unapprovedPrizeCount + unreadNotificationsCount;


  const handleNotificationDropdownOpen = async (open: boolean) => {
    if (open) {
      const hasUnread = !hasUnreadNotifications && notifications.some(n => n.unread);
      if (hasUnread) {
        try {
          await markNotificationsAsRead();
          setHasUnreadNotifications(true);
        } catch (err) {
          message.error('Ошибка при отметке уведомлений');
        }
      }
    }
  }

  useEffect(() => {
    const getUnfinishedTest = () => {
      if (!user?.id) {
        setUnfinishedTest(null);
        return;
      }
      const pathname = window.location.pathname;
      const isTestPage = pathname.startsWith('/monthly-test/') || pathname.startsWith('/test/');
      if (isTestPage) {
        setUnfinishedTest(null);
        return;
      }

      const session = getTestSession();
      if (!session) {
        setUnfinishedTest(null);
        return;
      }
      setUnfinishedTest({ ...session, secondsLeft: Math.max(0, session.expiresAt - Math.floor(Date.now() / 1000)) });
    };

    getUnfinishedTest();
    const intervalId = window.setInterval(getUnfinishedTest, 1000);
    return () => window.clearInterval(intervalId);
  }, [user?.id]);

  const formatSeconds = (seconds: number) => {
    const mins = Math.floor(Math.max(seconds, 0) / 60);
    const secs = Math.max(seconds, 0) % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const goToUnfinishedTest = () => {
    if (!unfinishedTest?.testId) return;
    const targetPath = unfinishedTest.type === 'monthly' ? `/monthly-test/${unfinishedTest.testId}` : `/test/${unfinishedTest.testId}`;
    window.location.href = targetPath;
  };

  const closeUnfinishedTestModal = () => {
    deleteTestSession();
    setUnfinishedTest(null);
  };

  return (
    <BrowserRouter>
      <ConfirmationModal
        visible={Boolean(user?.id && unfinishedTest)}
        title={'У вас есть незавершенный ' + (unfinishedTest?.type === 'monthly' ? 'ежемесячный' : '') + ' тест!'}
        description={`Время до окончания попытки: ${formatSeconds(unfinishedTest?.secondsLeft ?? 0)}`}
        confirmText="Перейти"
        cancelText="Закрыть"
        onConfirm={goToUnfinishedTest}
        onCancel={closeUnfinishedTestModal}
      />
      <Layout className="layout" style={{ minHeight: '100vh', outline: 'none' }}>
        <Row style={{ backgroundColor: '#A40207' }}>
          <Col xs={margin[0]} sm={margin[1]} md={margin[2]} lg={margin[3]} xl={margin[4]}></Col>
          <Col xs={content[0]} sm={content[1]} md={content[2]} lg={content[3]} xl={content[4]}>
            <Header style={{ backgroundColor: '#A40207' }}>
              <MainMenu
                isAdmin={isAdmin}
                isAdminMode={isAdminMode}
                setIsAdminMode={setIsAdminMode}
                adminMenuItems={adminMenuItems}
                noticesCount={noticesCount}
                notifications={notifications}
                user={user}
                handleNotificationDropdownOpen={handleNotificationDropdownOpen}
                mainMenuItems={mainMenuItems}
                unapprovedPrizeCount={unapprovedPrizeCount}
              />
            </Header>
          </Col>
          <Col xs={margin[0]} sm={margin[1]} md={margin[2]} lg={margin[3]} xl={margin[4]}></Col>
        </Row>
        <Row style={{ flex: '1 0 auto', padding: '0 20px' }}>
          <Col xs={margin[0]} sm={margin[1]} md={margin[2]} lg={margin[3]} xl={margin[4]}></Col>
          <Col xs={content[0]} sm={content[1]} md={content[2]} lg={content[3]} xl={content[4]}>
            <Content>
              <div className="site-layout-content">
                <Routes>
                  {!user?.id
                      ? routeAuthItems().map((item:any) => (<Route key={item.path} path={item.path} element={item.element} />))
                      : routeItems(user).map((item:any) => (<Route key={item.path} path={item.path} element={item.element} />))}
                </Routes>
              </div>
            </Content>
          </Col>
          <Col xs={margin[0]} sm={margin[1]} md={margin[2]} lg={margin[3]} xl={margin[4]}></Col>
        </Row>
        <Row style={{ backgroundColor: '#A40207', display: 'flex', marginTop: '30px' }}>
          <Col xs={margin[0]} sm={margin[1]} md={margin[2]} lg={margin[3]} xl={margin[4]}></Col>
          <Col xs={content[0]} sm={content[1]} md={content[2]} lg={content[3]} xl={content[4]}>
            <Footer style={{backgroundColor: '#A40207', color: '#fff'}}>
              <span>Copyright ©{new Date().getFullYear()}</span>
              <a style={{color: 'white'}} href="mailto:safin_ar@resanta.ru">Техподдержка: safin_ar@resanta.ru</a>
            </Footer>
          </Col>
          <Col xs={margin[0]} sm={margin[1]} md={margin[2]} lg={margin[3]} xl={margin[4]}></Col>
        </Row>
      </Layout>
    </BrowserRouter>
  );
};

export default App;
